export interface SetPasswordCapability {
  tokenHash: string;
  type: 'invite' | 'magiclink' | 'recovery';
  email: string | null;
}

export interface SharedTreeAccessCapability {
  inviteToken: string | null;
  collaboratorToken: string | null;
}

let setPasswordCapability: SetPasswordCapability | null = null;
let sharedTreeAccessCapability: SharedTreeAccessCapability | null = null;

/**
 * Capture access capabilities before telemetry starts and immediately remove
 * them from the address bar. Values live only in module memory until consumed.
 */
export function captureAccessUrlCapabilities(): void {
  const url = new URL(window.location.href);
  let changed = false;

  if (url.pathname === '/set-password') {
    const tokenHash = url.searchParams.get('token_hash') ?? '';
    const rawType = url.searchParams.get('type');
    const type = rawType === 'magiclink' || rawType === 'recovery' ? rawType : 'invite';
    setPasswordCapability = {
      tokenHash,
      type,
      email: url.searchParams.get('email'),
    };
    for (const key of ['token_hash', 'type', 'email']) {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        changed = true;
      }
    }
  }

  if (/^\/tree\/shared\/[^/]+\/?$/.test(url.pathname)) {
    const inviteToken = url.searchParams.get('invite');
    const collaboratorToken = url.searchParams.get('collab');
    if (inviteToken || collaboratorToken) {
      sharedTreeAccessCapability = { inviteToken, collaboratorToken };
    }
    for (const key of ['invite', 'collab']) {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        changed = true;
      }
    }
  }

  if (changed) {
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  }
}

export function getSetPasswordCapability(): SetPasswordCapability | null {
  return setPasswordCapability;
}

export function clearSetPasswordCapability(): void {
  setPasswordCapability = null;
}

export function getSharedTreeAccessCapability(): SharedTreeAccessCapability | null {
  return sharedTreeAccessCapability;
}

export function clearSharedTreeAccessCapability(): void {
  sharedTreeAccessCapability = null;
}

/** Remove access capabilities from telemetry payload strings without changing
 * the functional browser URL or breaking reload/copy/OAuth flows. */
export function redactAccessCapabilitiesForTelemetry<T>(value: T): T {
  if (typeof value === 'string') {
    return value
      // Storage signed URLs are bearer capabilities: redact both object path and token.
      .replace(/(?:https?:\/\/[^\s"'<>/?#]+)?\/storage\/v1\/object\/sign\/id-documents\/[^\s"'<>]+/g, '[REDACTED_ID_DOCUMENT_URL]')
      .replace(/(\/tree\/shared\/)[^/?#\s"']+/g, '$1[REDACTED]')
      .replace(/([?&](?:invite|collab|token_hash)=)[^&#\s"']+/g, '$1[REDACTED]') as T;
  }
  if (Array.isArray(value)) {
    return value.map((item) => redactAccessCapabilitiesForTelemetry(item)) as T;
  }
  if (value && typeof value === 'object') {
    const prototype = Object.getPrototypeOf(value);
    if (prototype !== Object.prototype && prototype !== null) return value;
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .map(([key, item]) => [key, redactAccessCapabilitiesForTelemetry(item)]),
    ) as T;
  }
  return value;
}
